import { YampaWidgetError } from "../errors";
import type { EventHandler, EventMap, WidgetEvent } from "./types";

/**
 * EventEmitter - Base class for event-driven functionality
 *
 * Provides methods for subscribing to and emitting events.
 * Used as the base class for YampaWidget.
 */
export class EventEmitter {
  private readonly listeners: Map<WidgetEvent, Set<EventHandler>> = new Map();
  private errorHandler?: (error: Error, event: WidgetEvent) => void;

  /**
   * Set a custom error handler for event handler failures
   *
   * @param handler - Function to call when an event handler throws an error
   */
  setErrorHandler(handler: (error: Error, event: WidgetEvent) => void): void {
    this.errorHandler = handler;
  }

  /**
   * Subscribe to an event
   *
   * @param event - The event name to listen to
   * @param handler - The handler function to call when the event is emitted
   *
   * @example
   * ```typescript
   * widget.on('ready', () => {
   *   console.log('Widget is ready');
   * });
   * ```
   */
  on<T extends WidgetEvent>(event: T, handler: EventHandler): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(handler);
  }

  /**
   * Unsubscribe from an event
   *
   * @param event - The event name to stop listening to
   * @param handler - The handler function to remove
   *
   * @example
   * ```typescript
   * const handler = () => console.log('ready');
   * widget.on('ready', handler);
   * widget.off('ready', handler);
   * ```
   */
  off<T extends WidgetEvent>(event: T, handler: EventHandler): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Subscribe to an event once
   *
   * The handler will be automatically removed after being called once.
   *
   * @param event - The event name to listen to
   * @param handler - The handler function to call when the event is emitted
   *
   * @example
   * ```typescript
   * widget.once('ready', () => {
   *   console.log('Widget ready (only called once)');
   * });
   * ```
   */
  once<T extends WidgetEvent>(event: T, handler: EventHandler): void {
    const onceHandler = ((data: EventMap[T]) => {
      handler(data);
      this.off(event, onceHandler);
    }) as EventHandler;
    this.on(event, onceHandler);
  }

  protected emit<T extends WidgetEvent>(event: T, data: EventMap[T]): void {
    const handlers = this.listeners.get(event);
    if (!handlers) {
      return;
    }

    for (const handler of handlers) {
      try {
        handler(data);
      } catch (error) {
        this.handleHandlerError(error, event);
      }
    }
  }

  private handleHandlerError(error: unknown, event: WidgetEvent): void {
    const err =
      error instanceof Error
        ? error
        : new YampaWidgetError(
            `Error in event handler for ${event}`,
            "EVENT_HANDLER_ERROR"
          );

    if (this.errorHandler) {
      this.errorHandler(err, event);
    } else if (this.listeners.has("error")) {
      this.emitErrorEvent(err);
    }
  }

  private emitErrorEvent(err: Error): void {
    try {
      const errorHandlers = this.listeners.get("error");
      if (errorHandlers) {
        for (const errorHandler of errorHandlers) {
          errorHandler({
            error: err,
            code: err instanceof YampaWidgetError ? err.code : undefined,
          });
        }
      }
    } catch {
      // Prevent infinite loop if error handler also fails
    }
  }

  /**
   * Remove all event listeners
   *
   * @param event - Optional event name. If provided, only listeners for that event are removed.
   *                If not provided, all listeners are removed.
   *
   * @example
   * ```typescript
   * widget.removeAllListeners(); // Remove all listeners
   * widget.removeAllListeners('ready'); // Remove only 'ready' listeners
   * ```
   */
  removeAllListeners(event?: WidgetEvent): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }

  /**
   * Get the number of listeners for an event
   *
   * @param event - The event name
   * @returns The number of listeners subscribed to the event
   *
   * @example
   * ```typescript
   * const count = widget.listenerCount('ready');
   * console.log(`${count} listeners for 'ready' event`);
   * ```
   */
  listenerCount(event: WidgetEvent): number {
    return this.listeners.get(event)?.size || 0;
  }
}
